import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { FactType, Threat } from '@/api/rest/resources/conservation';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { CapsuleTrend } from '@/components';
import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { useFact } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useFact';
import { useProject } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useProject';
import { getAreaPct } from '@/pages/shared/projects/pages/project/pages/conservation/shared/utils/getAreaPct';
import { getChangePct } from '@/pages/shared/projects/pages/project/pages/conservation/shared/utils/getChangePct';
import { Area } from '@/utils/area';
import { formatUnit } from '@/utils/formatting';

const NO_DATA = '-';

export const TableTile = () => {
  const { t } = useTranslation();

  const baselineYear = useFact<number>('r1_conservation_current_year').value - 4;

  return (
    <div className='flex flex-col gap-8'>
      <ChartHeading
        heading={t('shared.ncaDetail.habitatIntactness.degradationDriversTable.chartHeading', { year: baselineYear })}
        className='w-full px-6 sm:px-0'
      />
      <DegradationDrivers />
    </div>
  );
};

const DegradationDrivers = () => {
  const { t } = useTranslation();

  const currentYear = useFact<number>('r1_conservation_current_year').value;
  const baselineYear = currentYear - 4;
  const years = Array.from({ length: currentYear - baselineYear + 1 }, (_, i) => (baselineYear + i).toString());

  const project = useProject().data;
  const dimension = project?.areaUnits === 'ha' ? 'hectares' : 'squareKilometers';

  const projectAreaInHectares = new Area({
    value: project.area,
    dimension,
  }).toHectares().value;

  const threatsData = useThreatData();

  return (
    <Stack>
      <div className='w-full overflow-x-auto'>
        <table
          className='typography-body2 w-full table-auto border-collapse overflow-hidden rounded-3xl bg-[#fbfbfb]'
          data-testid='degradation-drivers-table'
        >
          <thead>
            <tr className='border-b border-[#e0e0e0] [&>*]:typography-overline [&>*]:p-6 [&>*]:pb-4 [&>*]:text-text-secondary '>
              <th className='text-start'>
                {t('shared.ncaDetail.habitatIntactness.degradationDriversTable.degradationDrivers')}
              </th>
              {years.map((year) => (
                <th key={year} className='text-end'>
                  {year}
                </th>
              ))}
              <th className='text-end'>
                {t('shared.ncaDetail.habitatIntactness.degradationDriversTable.totalAreaPct')}
              </th>
              <th className='text-end'>
                {t('shared.ncaDetail.habitatIntactness.degradationDriversTable.yearsChange')}
              </th>
            </tr>
          </thead>
          <tbody className='[&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:border-[#e0e0e0]'>
            {threatsData.map((threat) => {
              const latestValue = threat.ts[`${currentYear}` as string] as number;
              const baselineValue = threat.ts[`${baselineYear}` as string] as number;

              const changePct = getChangePct({
                baseline: baselineValue,
                latest: latestValue,
              });
              const displayChangePct =
                typeof baselineValue !== 'number' || typeof latestValue !== 'number'
                  ? NO_DATA
                  : `${getDisplayNumber(changePct, undefined, { signDisplay: 'exceptZero' })} ${formatUnit(UnitEnum['%'])}`;
              const variant = changePct > 0 ? 'negative' : 'positive';

              const threatAreaValue = getAreaPct({
                area: latestValue,
                totalArea: projectAreaInHectares,
              });
              const threatAreaPercentage =
                threatAreaValue !== null
                  ? `${getDisplayNumber(threatAreaValue)} ${formatUnit(UnitEnum['%'])}`
                  : NO_DATA;

              return (
                <tr
                  key={threat.name}
                  className='text-text-primary [&>*]:typography-body2 hover:bg-neutral-black-4 [&>*]:px-6 [&>*]:py-2'
                >
                  <td>{t(`shared.projects.project.conservation.labels.${threat.name}`)}</td>
                  {years.map((year, index) => {
                    const area = Object.hasOwn(threat.ts, year) ? threat.ts[`${year}`] : undefined;
                    const displayArea =
                      area !== undefined && area !== null
                        ? new Area({
                            value: area,
                            dimension: 'hectares',
                          }).renderDisplay({ className: 'whitespace-nowrap' })
                        : NO_DATA;

                    return (
                      <td key={`${year}-${index}`} className='text-end'>
                        {displayArea}
                      </td>
                    );
                  })}
                  <td className='text-end'>{threatAreaPercentage}</td>
                  <td className='text-end'>
                    <CapsuleTrend variant={variant}>{displayChangePct}</CapsuleTrend>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Stack>
  );
};

type ThreatData = {
  name: Threat;
  value: number;
  ts: Record<string, number>;
};

const useGetThreatData = (threat: Threat): ThreatData => ({
  name: threat,
  value: useFact<number>(`r1_conservation_${threat}` as FactType)?.value,
  ts: useFact<Record<string, number>>(`r1_conservation_${threat}_ts` as FactType)?.value,
});

const useThreatData = (): ThreatData[] => {
  const threats = [
    // The edge threat is calculated from all the other threats. Hence, we don't show this in the UI
    useGetThreatData(Threat.built),
    useGetThreatData(Threat.crop),
    useGetThreatData(Threat.road),
    useGetThreatData(Threat.mine),
    useGetThreatData(Threat.treeloss),
    useGetThreatData(Threat.overgrazing),
    useGetThreatData(Threat.other),
  ];

  return threats
    .filter(({ value }) => value !== null && value !== undefined && value >= 0)
    .sort((a, b) => b.value - a.value) as {
    name: Threat;
    value: number;
    ts: Record<string, number>;
  }[];
};
